import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { FlowGridScope, FlowsGrid } from '../../app/content/FlowsGrid';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import {
  AngularToReactDataGridBridge,
  IDataGridPropsScope,
} from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { FlowContentType } from '../models/ContentTypes';
import { FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISubplace } from '../models/IPlace';
import { ISliceable } from '../models/ISliceable';
import { IFavoriteItem, IItemStatisticsInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Favorites } from '../resources/Favorites';
import { Flows } from '../resources/Flows';
import { IActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ErrorState } from '../services/ErrorState';
import { FilterPanelService } from '../services/FilterPanelService';
import { FlowActions } from '../services/FlowActions';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';

import { SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { FlowAllowedActions } from '../ServerApi/types/FlowAllowedActions';
import { IUsageInfo } from '../ServerApi/types/IUsageInfo';
import { FilterPanelTypeaheadScope, IFilterPanelContainerScope } from './FilterPanelCtrl';

export type FlowsScope =
  CommonDataGridColumns.IFavoriteColumnScope<IFavoriteItem & IItemStatisticsInfo<IUsageInfo>> &
  FlowGridScope &
  IDataGridPropsScope<FlowListItemInfo> &
  IActionsMenuScope<FlowListItemInfo, {}> &
  IFilterPanelContainerScope &
  FilterPanelTypeaheadScope &
  RefreshContentScope &
  SubplaceWithActionButtonsScope<FlowListItemInfo, FlowDetailInfo> & {
    // This is used by the selection menu.
    sliceable: ISliceable<FlowListItemInfo>;
    // Used for force update on actions menu
    onAllowedActionsUpdate: (object: any) => void;
  };

const flowsEmptyTemplateModel: IEmptyTemplateModel = {
  title: 'flowsEmptyMessage'
};

export class FlowsCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    'BrowserTitleService',
    'ErrorState',
    'Favorites',
    'FilterPanelService',
    'Flows',
    'FlowActions',
    'QueryService',
    'ServerService',
    'SorterFactory',
    'subplace',
  ];

  constructor(
    private $scope: FlowsScope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    BrowserTitleService: BrowserTitleService,
    ErrorState: ErrorState,
    Favorites: Favorites,
    filterPanelService: FilterPanelService,
    Flows: Flows,
    flowActions: FlowActions,
    QueryService: QueryService,
    private ServerService: ServerService,
    SorterFactory: SorterFactory,
    subplace: ISubplace<FlowListItemInfo>,
  ) {
    if (!ServerService.flowsOnSiteEnabled()) {
      ErrorState.go({
        detailsLocKey: 'pageDoesNotExist',
        redirectState: 'main.site.content.site.projects',
        redirectLocKey: 'backToContent'
      });
      return;
    }

    BrowserTitleService.setTitleWithPlace({translatablePlaceName: 'flows'});

    $scope.filterControls = {};

    const explicitFilterKeys = subplace.filterKeys();
    $scope.filterDefinitions = filterPanelService.getFilterDefinitions(explicitFilterKeys);

    $scope.getFieldValueQuery = (filterField: string) => {
      return subplace.filterValuesQuery(filterField).withMatchText('');
    };

    $scope.selectedItems = new Set<FlowListItemInfo>((item: FlowListItemInfo) => item.id);

    const actions = flowActions.getFlowsActionsWithRunAndDownload();
    const getActionTarget = (): IActionTarget<FlowListItemInfo, FlowDetailInfo> => {
      return {
        items: this.$scope.selectedItems,
        actionContext: { scope: this.$scope },
        allowedActions: this.$scope.allowedActions
      };
    };

    this.configureForReactDataGrid(actions, getActionTarget);

    $scope.sorter = SorterFactory.create(FlowContentType.defaultOrder);
    $scope.$on('$destroy', () => $scope.sorter.detach());

    $scope.refreshContent = () => {
      queryUpdater.refresh();
      $scope.$emit(SubplaceEvents.RefreshCounts, subplace.name);
    };

    $scope.addFavorite = function($scope: FlowsScope, Favorites: Favorites) {
      return (flow: FlowListItemInfo) => {
        flow.usageInfo.favoritesTotal++;
        return Favorites.addFlow(flow);
      };
    }($scope, Favorites);

    $scope.removeFavorite = function($scope: FlowsScope, Favorites: Favorites) {
      return (flow: FlowListItemInfo) => {
        flow.usageInfo.favoritesTotal--;
        return Favorites.removeFlow(flow);
      };
    }($scope, Favorites);

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    const queryUpdater: IQueryUpdater =
      QueryService.createQueryUpdaterFromSubplace(
        $scope,
        subplace,
        (resourceQuery) => {
          AngularToReactDataGridBridge.updateReactDataGridRowStore({
            scope: $scope,
            sliceable: resourceQuery
          });
          $scope.sliceable = resourceQuery;
          $scope.sorter.setOrder(resourceQuery.getOrder());
        }
      );

    queryUpdater.refresh();

    $scope.onAllowedActionsUpdate = angular.noop;
    $scope.allowedActions = null;
    $scope.$watch(
      () => ($scope.selectedItems.size() === 1) ? $scope.selectedItems.first().id : null,
      (id: string) => {
        $scope.allowedActions = null;
        if (_.isString(id)) {
          Flows.actions(id).then((allowedActions: FlowAllowedActions) => {
            $scope.allowedActions = allowedActions;
            $scope.onAllowedActionsUpdate($scope);
          });
        }
      }
    );
  }

  private configureForReactDataGrid(actions: IActionButton<FlowListItemInfo, {}>[],
                                    getActionTarget: () => IActionTarget<FlowListItemInfo, {}>) {
    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    const columns = FlowsGrid.columns(
      this.$scope,
      this.$translate.instant.bind(this.$translate),
      actions,
      getActionTarget,
      siteUrls);

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns: columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(flowsEmptyTemplateModel),
      canShowFilterPanel: FlowContentType.canShowFilterPanel,
    });

  }

}
